import { calendarDate } from '../dates';
/**
 * Says why a form will not submit, the same way on every form. When the
 * browser refuses a submission because a field is missing or malformed, each
 * such field is marked and its reason shown under it, any folded section
 * hiding it is opened, the first one is focused, and a summary is announced.
 * Without this the browser shows a bubble only on the first field it can
 * focus, and nothing at all when that field sits in a closed section, so the
 * button seems to do nothing. A field's message clears as soon as it is fixed.
 */
type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
const isControl = (target: EventTarget | null): target is Control =>
  target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement;

/** The field's own label text, without the text of the control inside it. */
export function fieldLabel(control: Control): string {
  const label = control.closest('label') ?? (control.id ? document.querySelector(`label[for="${CSS.escape(control.id)}"]`) : null);
  const aria = control.getAttribute('aria-label');
  if (!label) return aria ?? control.name ?? 'This field';
  const copy = label.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('input,select,textarea,small,.field-error').forEach(node => node.remove());
  return copy.textContent?.replace(/\s+/g, ' ').trim() || aria || 'This field';
}

/** Why the browser refuses this field, in PayRight's words. */
export function fieldProblem(control: Control): string {
  const validity = control.validity;
  if (validity.valueMissing) return control instanceof HTMLInputElement && control.type === 'checkbox' ? 'Choose this to continue'
    : control instanceof HTMLSelectElement || control.type === 'radio' ? 'Choose one' : 'Required';
  if (validity.typeMismatch) return control.type === 'email' ? 'Enter an email address, like name@company.com'
    : control.type === 'url' ? 'Enter a web address starting with https://' : 'Not in the right format';
  if (validity.patternMismatch) return control.title || 'Not in the right format';
  if (validity.tooShort) return `Use at least ${(control as HTMLInputElement).minLength} characters`;
  if (validity.tooLong) return `Use at most ${(control as HTMLInputElement).maxLength} characters`;
  const day = (value: string) => calendarDate(value);
  const dated = control.type === 'date';
  if (validity.rangeUnderflow) { const min = (control as HTMLInputElement).min; return dated ? `Use ${day(min)} or later` : `Must be ${min} or more`; }
  if (validity.rangeOverflow) { const max = (control as HTMLInputElement).max; return dated ? `Use ${day(max)} or earlier` : `Must be ${max} or less`; }
  if (validity.stepMismatch) {
    const places = ((control as HTMLInputElement).step.split('.')[1] ?? '').length;
    return places ? `Use at most ${places} decimal place${places === 1 ? '' : 's'}` : 'Use a whole number';
  }
  if (validity.badInput) return control.type === 'date' ? 'Enter a complete date' : 'Not a valid value';
  return control.validationMessage || 'Check this field';
}

function messageHolder(control: Control): HTMLElement {
  // A choice between options is one question: its message goes under the question, once.
  if (control instanceof HTMLInputElement && control.type === 'radio') return control.closest('fieldset') ?? control.closest('label') ?? control;
  return control.closest('label') ?? control.parentElement ?? control;
}

function clear(control: Control) {
  if (control.getAttribute('aria-invalid') !== 'true') return;
  control.removeAttribute('aria-invalid');
  messageHolder(control).removeAttribute('data-error');
}

export function installFormFeedback(root: Document = document) {
  let pending: Control[] = [];
  const live = root.createElement('div');
  live.setAttribute('role', 'alert');
  live.className = 'visually-hidden';
  root.body.appendChild(live);

  root.addEventListener('invalid', event => {
    if (!isControl(event.target)) return;
    // Our message replaces the browser's bubble, which it shows for one field at most.
    event.preventDefault();
    const control = event.target;
    for (let fold = control.closest('details'); fold; fold = fold.parentElement?.closest('details') ?? null) fold.open = true;
    control.setAttribute('aria-invalid', 'true');
    messageHolder(control).setAttribute('data-error', fieldProblem(control));
    // The browser reports each refused field as its own event, with microtasks run between them;
    // a timer runs once they all have, so the first field is focused and the summary names every one.
    if (!pending.length) setTimeout(() => {
      const fields = pending; pending = [];
      fields[0]?.focus();
      fields[0]?.scrollIntoView?.({ block: 'center' });
      live.textContent = `Check ${fields.length === 1 ? 'this field' : `these ${fields.length} fields`}: ${fields
        .map(field => `${fieldLabel(field)} — ${fieldProblem(field)}`).join('; ')}.`;
    }, 0);
    pending.push(control);
  }, true);

  const recheck = (event: Event) => { if (isControl(event.target) && event.target.validity.valid) clear(event.target); };
  root.addEventListener('input', recheck, true);
  root.addEventListener('change', recheck, true);
}
